import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { Seo } from "../components/layout/Seo";
import { Button } from "../components/ui/Button";
import { IsraelAssessmentForm } from "../components/forms/IsraelAssessmentForm";
import { isLocale, localizedPath } from "../lib/routes";

const opportunityPoints = [
  ["01", "EU-oriented transition", "Regulation, infrastructure and investment priorities are increasingly shaped by Moldova’s European integration path."],
  ["02", "Real operational pressure", "Energy cost, resilience, water efficiency and climate adaptation already affect farms, wineries, processors and industrial operators."],
  ["03", "Financing can change the economics", "European, national and institutional financing mechanisms can materially improve the viability of qualified projects."],
  ["04", "Specialist niches remain open", "Advanced storage, agrivoltaics, industrial energy systems and selected climate technologies are less commoditized than standard rooftop solar."]
];

const pathStages = [
  ["01", "MARKET SIGNAL", "Is there a real problem worth solving?", "Identify the sectors and operators where the problem already exists."],
  ["02", "USE CASE", "Where does the technology create measurable value?", "Translate product capability into a concrete local business case."],
  ["03", "LOCAL VALIDATION", "Do market participants confirm the assumptions?", "Test urgency, current alternatives, economics and buying behaviour."],
  ["04", "PILOT", "Can the first project be structured realistically?", "Define the site, stakeholders, scope, economics and implementation requirements."],
  ["05", "LOCAL REFERENCE", "Can the first deployment become credible proof?", "A working reference changes every conversation that follows."],
  ["06", "SCALE", "Is there a repeatable path beyond the first project?", "Financing, service, local partners and additional customers turn a pilot into a market."]
];

const primaryCapabilities = [
  ["Customer Discovery", "Test the problem with real market participants.", "Structured conversations help determine whether interest represents genuine demand, weak curiosity or no market at all."],
  ["Pilot Development", "Turn a validated use case into a project.", "We help structure the pilot environment, stakeholders, preliminary economics and implementation pathway."]
];
const secondaryCapabilities = [
  ["Market Intelligence", "Understand the market before entering it.", "Sector structure, relevant competitors, regulation, adoption barriers and commercial context specific to your technology."],
  ["Use-Case Validation", "Find where the technology makes economic sense.", "We connect product capability with a specific operational problem and define the conditions under which deployment becomes credible."],
  ["Regulatory Navigation", "Identify what can block implementation early.", "Documentation, permits, standards, certifications and local requirements are mapped before they become expensive surprises."],
  ["Financing Pathways", "Understand how the project may be financed.", "We identify relevant commercial financing, institutional programmes, subsidies and grant mechanisms where applicable."]
];

const fitQuestions = [
  ["TECHNOLOGY", "Does the technology already work?", "Commercial deployment is ideal.\nPaid pilots or technically mature systems may also qualify when the implementation risk is understood."],
  ["PROBLEM", "Does Moldova have the problem now?", "We look for existing operational pain, measurable cost or strategic urgency.\nA theoretical future problem is not enough."],
  ["ECONOMICS", "Can the economics work?", "The customer must have a viable path to ROI, financing or another credible economic justification."],
  ["LOCALIZATION", "Can it be localized?", "Documentation, installation, servicing, regulation and technical support must be achievable in Moldova."]
];

const sectors = [
  ["Energy Storage", "Behind-the-meter battery storage, resilience systems and intelligent energy management for commercial and industrial users.", "typ", "Peak costs", "Grid interruptions", "Energy resilience", "Solar optimization"],
  ["Agrivoltaics", "Systems combining agricultural production with solar generation and crop protection.", "env", "Vineyards", "Orchards", "Greenhouses", "High-value crops"],
  ["Industrial Energy", "Technologies reducing energy dependence in food and processing industries.", "ex", "Thermal storage", "Industrial electrification", "Energy optimization", "Process heat"],
  ["Climate & AgriTech", "Technologies improving resilience and resource efficiency.", "ex", "Water management", "Irrigation", "Crop protection", "Climate adaptation", "Resource monitoring"]
];
const sectorLabels: Record<string, string> = { typ: "Typical problems", env: "Typical environments", ex: "Examples" };

const outputs = [
  ["GO", "A credible use case exists.", "You know where to focus, who the first customer profile is and what must happen next."],
  ["REFINE", "The opportunity exists, but the original use case is wrong.", "We redefine the target segment, value proposition or implementation model."],
  ["WAIT", "The market may become attractive later.", "You leave with clear conditions to monitor instead of spending resources too early."],
  ["STOP", "The evidence does not justify market entry.", "A fast “no” can be more valuable than a twelve-month market-entry experiment."]
];

const principles = [
  ["Evidence before expansion", "We test assumptions before recommending significant market investment."],
  ["Local reality before presentation logic", "A market deck becomes useful only when real operators confirm what it says."],
  ["Long-term implementation before one-off introductions", "The value of a local partner is measured by what happens after the first meeting."]
];

const sectionPad = "py-[clamp(72px,9vw,128px)]";

function Head({ eyebrow, title, intro, dark = false, className = "" }: { eyebrow?: string; title: string; intro?: string; dark?: boolean; className?: string }) {
  return (
    <div className={`max-w-[760px] ${className}`}>
      {eyebrow && <p className={`${dark ? "eyebrow-sage" : "eyebrow"} mb-4`}>{eyebrow}</p>}
      <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">{title}</h2>
      {intro && <p className={`mt-5 text-lg leading-8 ${dark ? "text-brand-darkMuted" : "text-ink-secondary"}`}>{intro}</p>}
    </div>
  );
}

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function Israel() {
  const { locale } = useParams();
  const activeLocale = isLocale(locale) ? locale : "en";

  return (
    <>
      <Seo
        title="Israeli Technology Market Entry to Moldova"
        description="TransitionBridge helps Israeli climate-tech, energy-tech and agri-tech companies validate Moldova market fit, identify real use cases and structure credible pilot projects."
      />

      {/* BLOCK 1 — HERO */}
      <section className="petrol-section overflow-hidden pt-[calc(var(--header-height)+40px)] text-brand-darkText">
        <div className="container-grid grid items-center gap-12 pb-16 lg:grid-cols-[1fr_1fr] lg:gap-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="eyebrow-sage mb-5">FOR ISRAELI CLIMATE, ENERGY &amp; AGRI-TECH COMPANIES</p>
            <h1 className="text-balance text-[clamp(2.2rem,5.5vw,3.6rem)] font-extrabold leading-[1.04]">Test Moldova before you invest in Moldova.</h1>
            <p className="mt-7 max-w-[620px] text-lg leading-8 text-brand-darkMuted md:text-xl">Moldova can be a promising market for selected Israeli technologies — when the problem, economics and local implementation path are real.</p>
            <p className="mt-3 max-w-[620px] text-lg leading-8 text-brand-darkMuted md:text-xl">We help you test those assumptions before you commit significant time, capital or management attention.</p>
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-brand-cyan">Demand · Economics · Localization · Pilot</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button type="button" onClick={() => scrollToId("moldova-fit")}>Assess Moldova Fit</Button>
              <Button type="button" variant="darkGhost" onClick={() => scrollToId("market-entry-path")}>See the Market Entry Path</Button>
            </div>
            <p className="mt-7 text-sm text-brand-darkMuted">From technology hypothesis to a credible first local project.</p>
          </motion.div>

          <div className="relative">
            <img src="/assets/israel/israel-01-hero-bridge.webp" alt="Israeli technology and renewable energy infrastructure connected with Moldova’s agricultural landscape." className="aspect-[4/3] w-full rounded-3xl object-cover shadow-soft" />
          </div>
        </div>
      </section>

      {/* BLOCK 2 — WHY MOLDOVA */}
      <section id="why-moldova" className={`bg-bg-primary ${sectionPad}`}>
        <div className="container-grid">
          <Head eyebrow="WHY MOLDOVA" title="A small market can still be the right first market." />
          <div className="mt-6 max-w-[820px] leading-8 text-ink-secondary">
            <p>Moldova is moving through a period of energy, agricultural and infrastructure modernization while aligning more closely with European standards and investment priorities.</p>
            <p className="mt-4">For selected technologies, that creates an unusual opportunity: entering while categories are still forming instead of waiting until the market becomes crowded.</p>
          </div>
        </div>
        <div className="container-grid mt-12">
          <img src="/assets/israel/israel-02-moldova-opportunity.webp" loading="lazy" decoding="async" alt="Modern Moldovan winery and agricultural landscape using solar energy, storage and greenhouse infrastructure." className="aspect-[16/9] w-full rounded-3xl object-cover shadow-soft" />
        </div>
        <div className="container-grid mt-12 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
          {opportunityPoints.map(([num, title, body]) => (
            <div key={num} className="border-t border-line pt-5">
              <p className="mb-2 text-sm font-bold text-brand-blue/60">{num}</p>
              <h3 className="mb-2 text-lg font-bold">{title}</h3>
              <p className="text-sm leading-6 text-ink-secondary">{body}</p>
            </div>
          ))}
        </div>
        <div className="container-grid mt-12 max-w-[820px]">
          <p className="border-l-4 border-brand-blue ps-6 leading-7 text-ink-secondary">Moldova is not automatically the right market for every technology. <span className="block font-semibold text-ink-primary">That is why validation comes first.</span></p>
        </div>
      </section>

      {/* BLOCK 3 — MARKET ENTRY PATH */}
      <section id="market-entry-path" className={`petrol-section ${sectionPad}`}>
        <div className="container-grid">
          <Head dark eyebrow="THE MARKET ENTRY PATH" title="From technology to a credible local reference." intro="A new market should not begin with a distribution agreement. It should begin with evidence. The path below is designed to reduce uncertainty step by step." />

          <div className="mt-12 hidden lg:block">
            <div className="relative overflow-hidden rounded-3xl">
              <img src="/assets/israel/israel-03-market-entry-path.webp" loading="lazy" decoding="async" alt="Visual market-entry pathway connecting Israeli technology with Moldova." className="h-[580px] w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 h-[70%] bg-gradient-to-t from-[#0b1e2e]/95 via-[#0b1e2e]/70 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 px-8 pb-8">
                <div className="grid grid-cols-6 gap-3">
                  {pathStages.map(([num, title, q]) => (
                    <div key={num} className="relative">
                      <span className="mb-3 grid h-9 w-9 place-items-center rounded-full bg-brand-cyan text-xs font-bold text-[#04202e] ring-4 ring-[#12afcb]/25">{num}</span>
                      <div className="border-l-2 border-brand-cyan/60 pl-3">
                        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-cyan">{title}</p>
                        <p className="mt-1 text-sm font-semibold leading-snug text-brand-darkText">{q}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile / tablet: vertical timeline */}
          <div className="relative mt-12 lg:hidden">
            <div className="absolute left-[15px] top-3 bottom-3 w-px bg-brand-cyan/30" aria-hidden="true" />
            <ol className="grid gap-10">
              {pathStages.map(([num, title, q, support]) => (
                <li key={num} className="relative pl-12">
                  <span className="absolute left-0 top-0 grid h-8 w-8 place-items-center rounded-full bg-brand-cyan text-xs font-bold text-[#04202e]">{num}</span>
                  <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-cyan">{title}</p>
                  <p className="mt-1 text-base font-semibold text-brand-darkText">{q}</p>
                  <p className="mt-2 text-sm leading-6 text-brand-darkMuted">{support}</p>
                </li>
              ))}
            </ol>
          </div>

          <p className="mt-9 text-center text-2xl font-bold text-brand-cyan md:text-3xl">Evidence first. Expansion second.</p>
        </div>
      </section>

      {/* BLOCK 4 — WHAT TB ACTUALLY DOES */}
      <section className={`bg-bg-primary ${sectionPad}`}>
        <div className="container-grid">
          <Head eyebrow="OUR ROLE" title="The local layer between technology and implementation." />
          <div className="mt-6 max-w-[760px] leading-8 text-ink-secondary">
            <p>Israeli companies usually understand their technology better than anyone.</p>
            <p className="mt-4">What they often lack in a new market is reliable local context: who has the problem, how decisions are made, what can block deployment, how the economics work and who must participate locally.</p>
          </div>
          <p className="mt-7 max-w-[760px] text-2xl font-bold text-ink-primary">TransitionBridge builds that missing layer.</p>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {primaryCapabilities.map(([title, lead, body]) => (
              <div key={title} className="rounded-2xl border border-brand-cyan/30 bg-gradient-to-br from-surface to-bg-secondary p-8 shadow-soft md:p-10">
                <h3 className="text-2xl font-bold">{title}</h3>
                <p className="mt-3 font-semibold leading-7 text-ink-primary">{lead}</p>
                <p className="mt-3 leading-7 text-ink-secondary">{body}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {secondaryCapabilities.map(([title, lead, body]) => (
              <div key={title} className="border-t border-line pt-5">
                <h3 className="text-base font-bold">{title}</h3>
                <p className="mt-2 text-sm font-semibold leading-5 text-ink-primary">{lead}</p>
                <p className="mt-2 text-sm leading-6 text-ink-secondary">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCK 5 — TECHNOLOGY / MARKET FIT */}
      <section className={`bg-bg-secondary ${sectionPad}`}>
        <div className="container-grid grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <div>
            <img src="/assets/israel/israel-04-technology-fit.webp" loading="lazy" decoding="async" alt="Technology streams converging into practical applications in Moldova." className="aspect-square w-full rounded-3xl object-cover shadow-soft" />
          </div>
          <div>
            <Head eyebrow="MOLDOVA FIT" title="Four questions matter more than market size." intro="A market can look attractive on paper and still be wrong for a specific technology. Before recommending Moldova, we want four questions answered." />
            <div className="mt-8 grid gap-6">
              {fitQuestions.map(([label, q, body], i) => (
                <div key={label} className="grid gap-2 border-t border-line pt-5 md:grid-cols-[auto_1fr] md:gap-6">
                  <span className="inline-flex h-fit w-fit rounded-full border border-brand-cyan/40 bg-brand-cyan/10 px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.12em] text-brand-cyanDark">{label}</span>
                  <div>
                    <h3 className="text-lg font-bold">{q}</h3>
                    <p className="mt-2 text-sm leading-6 text-ink-secondary">{[...body.split("\n").filter(Boolean)].map((l) => <span key={l} className="block">{l}</span>)}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 rounded-xl border-l-4 border-brand-blue bg-surface px-5 py-4 font-semibold text-ink-primary">If one answer is unclear, that becomes the first validation question.</p>
          </div>
        </div>
      </section>

      {/* BLOCK 6 — STRONGEST FIT SECTORS */}
      <section className={`bg-bg-primary ${sectionPad}`}>
        <div className="container-grid">
          <Head eyebrow="WHERE WE SEE THE STRONGEST FIT" title="Selected technologies. Concrete operational problems." intro="TransitionBridge is deliberately focused. We are most interested in technologies that solve measurable problems in energy-intensive agriculture, food production and climate resilience." />
        </div>
        <div className="container-grid mt-12">
          <img src="/assets/israel/israel-05-focus-sectors.webp" loading="lazy" decoding="async" alt="Agrivoltaics, battery storage, industrial energy and agricultural infrastructure in Moldova." className="h-56 w-full rounded-3xl object-cover shadow-soft sm:h-72 md:h-96 lg:h-[520px]" />
        </div>
        <div className="container-grid mt-10 grid gap-5 lg:grid-cols-4">
          {sectors.map(([title, lead, kind, ...areas], i) => (
            <div key={title} className="rounded-2xl border border-line bg-surface p-6 md:p-7">
              <p className="mb-3 text-sm font-bold text-brand-blue/60">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="text-xl font-bold">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-ink-secondary">{lead}</p>
              <p className="mt-5 text-xs font-bold uppercase tracking-[0.12em] text-ink-secondary">{sectorLabels[kind]}</p>
              <ul className="mt-2 grid gap-1.5 text-sm leading-6 text-ink-secondary">
                {areas.map((a) => <li key={a} className="flex items-start gap-2"><span className="mt-2 block h-1.5 w-1.5 shrink-0 rounded-full bg-brand-cyan/70" aria-hidden="true" />{a}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="container-grid mt-8">
          <p className="max-w-[760px] text-sm leading-6 text-ink-secondary">Technology categories shown here reflect areas of interest, not existing representation agreements.</p>
        </div>
      </section>

      {/* BLOCK 7 — WHAT A TECHNOLOGY COMPANY GETS */}
      <section className="petrol-section pt-[clamp(72px,9vw,120px)] pb-[clamp(48px,6vw,84px)]">
        <div className="container-grid">
          <Head dark eyebrow="THE OUTPUT" title="A market decision, not another market report." intro="The purpose of validation is not to produce a presentation. It is to help your team make a better decision about what to do next." />
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {outputs.map(([label, title, body]) => (
              <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-8">
                <p className="mb-4 inline-flex rounded-full border border-brand-cyan/40 bg-brand-cyan/10 px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.14em] text-brand-cyan">{label}</p>
                <h3 className="text-xl font-bold text-brand-darkText">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-brand-darkMuted">{body}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 max-w-4xl text-balance text-3xl font-bold leading-tight text-brand-darkText md:text-5xl">A fast “no” is cheaper than a slow mistake.</p>
        </div>
      </section>

      {/* BLOCK 8 — WHY TRANSITIONBRIDGE */}
      <section className={`bg-bg-primary ${sectionPad}`}>
        <div className="container-grid grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Head eyebrow="WHY TRANSITIONBRIDGE" title="The part of market entry that cannot be done remotely." />
            <div className="mt-6 max-w-[640px] space-y-4 leading-8 text-ink-secondary">
              <p>Technology can be evaluated from Israel. A market cannot.</p>
              <p>Someone still has to understand who has the problem, how decisions are made, what can block implementation, how the economics work and what the first credible project should look like.</p>
            </div>
            <p className="mt-6 max-w-[640px] text-xl font-bold text-ink-primary">TransitionBridge builds that local implementation layer.</p>
          </div>
          <div className="grid gap-6">
            {principles.map(([title, body]) => (
              <div key={title} className="border-l-4 border-brand-cyan pl-6">
                <h3 className="text-lg font-bold">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-ink-secondary">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCK 9 — MOLDOVA FIT ASSESSMENT */}
      <section id="moldova-fit" className={`bg-bg-secondary ${sectionPad}`}>
        <div className="container-grid">
          <Head eyebrow="MOLDOVA FIT ASSESSMENT" title="Is Moldova worth testing for your technology?" intro="Answer a few questions about your company, technology and international readiness. We use them to understand whether there is a credible Moldova hypothesis worth exploring." />
          <div className="mt-6 max-w-2xl">
            <p className="text-sm text-ink-secondary">Estimated time: 3 minutes · No commitment · Draft saved locally</p>
          </div>
          <div className="mt-8 max-w-[1000px]">
            <IsraelAssessmentForm />
          </div>
        </div>
      </section>

      {/* BLOCK 10 — FINAL IMAGE + CTA */}
      <section className={`petrol-section ${sectionPad}`}>
        <div className="container-grid">
          <img src="/assets/israel/israel-06-final-cta.webp" loading="lazy" decoding="async" alt="Working renewable-energy and agricultural project in Moldova at sunset." className="w-full rounded-3xl object-cover shadow-soft" />
        </div>
        <div className="container-grid mt-12">
          <div className="max-w-[820px]">
            <h2 className="text-balance text-3xl font-bold leading-[1.08] text-brand-darkText md:text-[2.6rem]">Moldova may be a market. First, make it a hypothesis we can test.</h2>
            <p className="mt-6 max-w-[760px] text-lg leading-8 text-brand-darkMuted">The first step does not require a distributor, an investment or a market-entry plan.</p>
            <p className="mt-2 max-w-[760px] text-lg leading-8 text-brand-darkMuted">It requires a clear question and enough evidence to answer it.</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button type="button" onClick={() => scrollToId("moldova-fit")}>Assess Moldova Fit</Button>
              <Button type="button" variant="secondary" to={localizedPath(activeLocale, "contact")}>Talk to TransitionBridge</Button>
            </div>
          </div>
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-bg-primary/95 p-3 backdrop-blur md:hidden">
        <Button className="w-full" type="button" onClick={() => scrollToId("moldova-fit")}>Assess Moldova Fit</Button>
      </div>
    </>
  );
}
